import {
    Box,
    Checkbox,
    FormControlLabel,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';
import Contact from './Contact';
import { ContactType, contactTypes } from './types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface Props {
    data: Record<string, any>;
    onChange: (field: keyof ContactType, value: any, index?: number) => void;
    errors: { [key: string]: string };
}

const ContactsInformation = (props: Props) => {
    const contacts: ContactType[] = props.data.contacts;

    const isPurchasingSameAsPrimary = contacts[1]?.sameAsPrimary ?? false;
    const isAccountsSameAsPrimary = contacts[2]?.sameAsPrimary ?? false;

    const handleChange = (
        index: number,
        field: keyof ContactType,
        value: any
    ) => {
        props.onChange(field, value, index);
    };

    const handleSameAsPrimaryChange = (
        index: number,
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const isChecked = event.target.checked;
        props.onChange('sameAsPrimary', isChecked, index);

        const primary = contacts[0];
        const blank: Partial<ContactType> = {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
        };

        const source = isChecked ? primary : blank;

        (['firstName', 'lastName', 'email', 'phone'] as (keyof ContactType)[]).forEach(
            (k) => props.onChange(k, (source as any)[k], index)
        );
    };

    return (
        <Box sx={{ padding: '20px' }}>
            <Stack
                width={{ xs: '100%', sm: '90%', md: '80%', lg: '60%' }}
                maxWidth='800px'
                minWidth='200px'
            >
                <Typography style={{ color: '#808080', fontSize: 28 }}>
                    Contact Information
                </Typography>

                {/* Primary Contact */}
                <Box sx={{ marginBottom: 4, position: 'relative' }}>
                    <Typography style={{ fontSize: 18 }} marginTop={4}>
                        {contactTypes.Primary.label}
                    </Typography>
                    <Tooltip
                        title='Please provide primary contact for this account'
                        sx={{ position: 'absolute', top: 28, left: 150 }}
                    >
                        <IconButton size='small'>
                            <InfoOutlinedIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                    <Contact
                        contact={contacts[0]}
                        handleChange={(field, value) =>
                            handleChange(0, field as keyof ContactType, value)
                        }
                        errors={props.errors}
                        errorKey='primaryContact'
                    />
                </Box>

                {/* Purchasing Department */}
                <Box sx={{ marginBottom: 4, position: 'relative' }}>
                    <Stack direction='row' alignItems='center' marginTop={4}>
                        <Typography style={{ fontSize: 18 }} marginRight={8}>
                            {contactTypes.Purchasing.label}
                        </Typography>
                        <Tooltip
                            title='If your facility has designated staff to handle purchasing please fill out the fields below'
                            sx={{ position: 'absolute', top: 35, left: 200 }}
                        >
                            <IconButton size='small'>
                                <InfoOutlinedIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isPurchasingSameAsPrimary}
                                    onChange={(e) => handleSameAsPrimaryChange(1, e)}
                                />
                            }
                            label='Same as Primary'
                        />
                    </Stack>
                    {!isPurchasingSameAsPrimary && (
                        <Contact
                            contact={contacts[1]}
                            handleChange={(field, value) =>
                                handleChange(1, field as keyof ContactType, value)
                            }
                            errors={props.errors}
                            errorKey='purchasingContact'
                        />
                    )}
                </Box>

                {/* Accounts Payable */}
                <Box sx={{ marginBottom: 4, position: 'relative' }}>
                    <Stack direction='row' alignItems='center' marginTop={4}>
                        <Typography style={{ fontSize: 18 }} marginRight={8}>
                            {contactTypes.AccountsPayable.label}
                        </Typography>
                        <Tooltip
                            title='If your facility has designated staff to handle accounts payable please fill out the fields below'
                            sx={{ position: 'absolute', top: 30, left: 160 }}
                        >
                            <IconButton size='small'>
                                <InfoOutlinedIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isAccountsSameAsPrimary}
                                    onChange={(e) => handleSameAsPrimaryChange(2, e)}
                                />
                            }
                            label='Same as Primary'
                        />
                    </Stack>
                    {!isAccountsSameAsPrimary && (
                        <Contact
                            contact={contacts[2]}
                            handleChange={(field, value) =>
                                handleChange(2, field as keyof ContactType, value)
                            }
                            errors={props.errors}
                            errorKey='accountsPayableContact'
                        />
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default ContactsInformation;
